import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/i18n/LanguageContext";
import { useAuth } from "@/auth/AuthContext";
import { LayoutDashboard, Settings, LogOut, ChevronDown, User } from "lucide-react";
import GoogleAvatar from "@/components/GoogleAvatar";

const COPY = {
  en: { dashboard: "Dashboard", studio: "Studio", settings: "Settings", logout: "Sign out", greet: "Signed in as" },
  ru: { dashboard: "Кабинет", studio: "Студия", settings: "Настройки", logout: "Выйти", greet: "Вход выполнен как" },
  es: { dashboard: "Panel", studio: "Estudio", settings: "Ajustes", logout: "Cerrar sesión", greet: "Sesión iniciada como" },
};

export default function ProfileMenu({ onOpenSettings }) {
  const { lang } = useLang();
  const c = COPY[lang] || COPY.en;
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  if (!user || user === false) return null;

  return (
    <div className="relative" ref={wrapRef}>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="profile-menu-btn"
        className="flex items-center gap-2 pl-1 pr-2 py-1 border border-[#0a0a0a] hover:bg-[#0a0a0a] hover:text-white transition-colors group"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <GoogleAvatar user={user} size={28} />
        <ChevronDown size={12} className={open ? "rotate-180 transition-transform" : "transition-transform"} />
      </button>

      {open && (
        <div
          data-testid="profile-menu-dropdown"
          className="absolute right-0 top-full mt-2 min-w-[240px] bg-white border border-[#0a0a0a] shadow-[6px_6px_0_0_rgba(10,10,10,1)] z-50"
        >
          <div className="px-4 py-3 border-b border-[#0a0a0a]">
            <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#8a8a8a]">{c.greet}</div>
            <div className="mt-1 font-mono text-sm text-[#0a0a0a] truncate">{user.name || "—"}</div>
            <div className="font-mono text-[10px] text-[#8a8a8a] truncate">{user.email}</div>
          </div>

          <Link
            to="/dashboard"
            onClick={() => setOpen(false)}
            data-testid="profile-menu-dashboard"
            className="flex items-center gap-3 px-4 py-3 font-mono text-xs uppercase tracking-[0.2em] text-[#0a0a0a] hover:bg-[#f5f5f5] transition-colors"
          >
            <LayoutDashboard size={14} />
            {user.role === "admin" ? c.studio : c.dashboard}
          </Link>

          <button
            onClick={() => { setOpen(false); onOpenSettings(); }}
            data-testid="profile-menu-settings"
            className="w-full flex items-center gap-3 px-4 py-3 font-mono text-xs uppercase tracking-[0.2em] text-[#0a0a0a] hover:bg-[#f5f5f5] transition-colors text-left"
          >
            <Settings size={14} />
            {c.settings}
          </button>

          <button
            onClick={() => { setOpen(false); logout(); }}
            data-testid="profile-menu-logout"
            className="w-full flex items-center gap-3 px-4 py-3 border-t border-[#0a0a0a] font-mono text-xs uppercase tracking-[0.2em] text-[#ff006e] hover:bg-[#0a0a0a] hover:text-[#ff006e] transition-colors text-left"
          >
            <LogOut size={14} />
            {c.logout}
          </button>
        </div>
      )}
    </div>
  );
}
